import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { createFileRoute } from '@tanstack/react-router'
import { useEffect, useRef, useState, type ReactElement } from 'react'
// Der Steuer-Wortlaut kommt aus src/shared/vat.ts — derselben Quelle, aus der das PDF ihn druckt und
// submitInvoice ihn als `legal_text` einfriert. (Nicht aus src/worker/pdf/rechnung.ts: dieses Modul
// zieht pdf-lib nach und gehoert nicht in ein Browser-Bundle.)
import { VAT_19_LABEL, vatLegalText } from '../../../shared/vat'
// Die beiden Antworttypen kommen aus src/client/api.ts, der einen Beschreibung der Server-Antworten
// (Part 23 Task 80). Eine zweite Beschreibung hier wäre eine Kopie, die driftet.
import { api, type AbrechnungAnsicht, type AbrechnungZeile } from '../../api'
import { RequireSession } from '../../auth'
import { Betrag } from '../../components/Betrag'
import { Knopf } from '../../components/Knopf'
import { Tabelle } from '../../components/Tabelle'
import { TabellenZustand, Zustand } from '../../components/Zustand'
import { datum } from '../../format'
import { fehlerText } from '../login'

export interface AbrechnenSuche {
  tc?: string
  /** '1': beim Öffnen alles Abrechenbare vorauswählen — der Knopf „Rechnung erstellen“ im Dashboard */
  alle?: string
}

/**
 * Der Entwurf, gelesen mit einem GET. Der Server antwortet mit derselben `AbrechnungView`, die der
 * Upsert zurückgibt — nur ohne etwas zu verschieben und ohne eine Audit-Zeile zu schreiben. Der
 * zuvor erwogene Weg, den Entwurf über den idempotenten `POST /api/me/rechnungen` zu „lesen", ist
 * ausdrücklich nicht zulässig: er legt bei jedem Seitenaufruf Provisionszeilen zwischen `open` und
 * `in_draft` um und schreibt `invoice.draft_upsert` ins Protokoll. Auf dem Bildschirm, auf dem
 * jemand sein eigenes Geld abrechnet, ist ein Lesevorgang, der schreibt, nicht hinnehmbar.
 *
 * Die Route antwortet mit `{ abrechnung: … }` (src/worker/routes/billing.ts); der `POST` daneben
 * antwortet mit der Ansicht selbst. Deshalb wird hier ausgepackt und dort nicht.
 */
const entwurfLesen = async (tc: string): Promise<AbrechnungAnsicht> =>
  (await api.get<{ abrechnung: AbrechnungAnsicht }>('/api/me/abrechnung', { tenant_contractor_id: tc })).abrechnung

function AbrechnungFormular({ tc, ansicht, alleVorwaehlen = false }: { tc: string; ansicht: AbrechnungAnsicht; alleVorwaehlen?: boolean }): ReactElement {
  const queryClient = useQueryClient()
  const [einverstanden, setEinverstanden] = useState(false)
  const [eingereicht, setEingereicht] = useState(false)

  const auswahl = useMutation({
    mutationFn: (ids: string[]) =>
      api.post<AbrechnungAnsicht>('/api/me/rechnungen', { tenant_contractor_id: tc, commission_ids: ids }),
    // Geschrieben wird die Antwort des Servers, nie ein geratenes Ergebnis.
    onSuccess: (neu) => queryClient.setQueryData(['me', 'abrechnung', tc], neu),
  })

  const einreichen = useMutation({
    mutationFn: async () => {
      // Kein Rückfall auf einen leeren Pfad: ohne Entwurf gibt es keine Adresse, an die eingereicht
      // werden könnte, und ein `/api/me/rechnungen//submit` wäre ein stiller Unsinn statt eines
      // Fehlers. Der Knopf ist in diesem Fall ohnehin gesperrt (siehe `gruende`).
      const id = ansicht.invoice_id
      if (id === null) throw new Error('Es gibt noch keinen Entwurf, der eingereicht werden könnte.')
      return api.post(`/api/me/rechnungen/${encodeURIComponent(id)}/submit`, { consent: true })
    },
    onSuccess: () => setEingereicht(true),
  })

  const gewaehlteZeilen = ansicht.lines.filter((zeile) => zeile.ausgewaehlt)
  const gewaehlteIds = gewaehlteZeilen.map((zeile) => zeile.commission_id)

  // Ein Klick aus dem Dashboard: alles, was abrechenbar ist, steht schon auf dem Entwurf. Genau einmal —
  // wer danach eine Zeile abwählt, will sie nicht zurück.
  const abrechenbareIds = ansicht.lines.filter((zeile) => zeile.abrechenbar).map((zeile) => zeile.commission_id)
  const schonVorgewaehlt = useRef(false)
  useEffect(() => {
    if (!alleVorwaehlen || schonVorgewaehlt.current) return
    schonVorgewaehlt.current = true
    if (abrechenbareIds.length > 0 && gewaehlteIds.length === 0) auswahl.mutate(abrechenbareIds)
  })

  function umschalten(zeile: AbrechnungZeile): void {
    const neu = zeile.ausgewaehlt
      ? gewaehlteIds.filter((id) => id !== zeile.commission_id)
      : [...gewaehlteIds, zeile.commission_id]
    auswahl.mutate(neu)
  }

  // Jede dieser Bedingungen ist eine Angabe des Servers, keine Regel dieses Screens.
  const gruende: string[] = []
  if (ansicht.invoice_id === null) gruende.push('Es gibt noch keinen Entwurf.')
  // Zwei Ablehnungen, zwei Sätze — der Server unterscheidet `keine_zeilen` von
  // `betrag_nicht_positiv`, und wer nichts angehakt hat, hat keine Rückforderung, über die man ihm
  // etwas erzählen müsste.
  if (gewaehlteZeilen.length === 0) {
    gruende.push('Es ist keine Position ausgewählt. Bitte haken Sie mindestens eine Provision an.')
  } else if (ansicht.net_cents <= 0) {
    gruende.push(
      'Diese Auswahl ergibt keinen positiven Betrag. Eine Rechnung über 0 € oder weniger ist nicht möglich — '
      + 'die offene Rückforderung wird mit Ihrer nächsten Provision verrechnet, oder Ihr Auftraggeber gleicht sie aus.',
    )
  }
  if (!ansicht.geprueft) {
    gruende.push('Dieses Steuerprofil ist noch nicht geprüft; der Plattform-Administrator muss es freigeben.')
  }
  const bereit = gruende.length === 0 && ansicht.profil.complete

  /*
   * Die Vorschau: `GET /api/me/rechnungen/:invId/preview.pdf` existiert (Aufgabe 101,
   * `previewInvoice` in services/billing.ts) und rendert genau einen Entwurf, mit Wasserzeichen und
   * „ENTWURF" an der Stelle der Nummer. Sie hat aber dieselben vier Vorbedingungen wie das
   * Einreichen, ohne `geprueft`: Entwurf, vollständiges Profil, mindestens eine gewählte Zeile, alle
   * gewählten abrechenbar, Netto positiv. Sonst antwortet sie 422 — und ein <iframe> auf eine 422
   * ist ein weißes Rechteck, das aussieht wie ein Absturz. Deshalb steht hier ein Link, der erst
   * erscheint, wenn der Server die Seite auch wirklich zeichnen kann, und sonst ein Satz.
   */
  const vorschauMoeglich = ansicht.invoice_id !== null
    && ansicht.profil.complete
    && gewaehlteZeilen.length > 0
    && gewaehlteZeilen.every((zeile) => zeile.abrechenbar)
    && ansicht.net_cents > 0

  if (eingereicht) {
    return (
      <>
        <p role="status" className="mt-6 max-w-prose">
          Ihre Abrechnung ist eingereicht. {ansicht.tenant_name} prüft sie; die Rechnungsnummer wird erst
          bei der Freigabe vergeben. Solange niemand entschieden hat, können Sie die Einreichung
          zurückziehen.
        </p>
        <p className="mt-6">
          <a className="text-[var(--accent)] underline" href="/me/rechnungen">Zu meinen Abrechnungen</a>
        </p>
      </>
    )
  }

  return (
    <>
      <p className="mt-2 text-[var(--ink-muted)]">
        {ansicht.empfaenger} an {ansicht.tenant_name}
      </p>

      {auswahl.error !== null && (
        <p role="alert" className="mt-6 border-l-2 border-[var(--alert)] pl-3 text-[var(--alert)]">
          {fehlerText(auswahl.error)}
        </p>
      )}
      {einreichen.error !== null && (
        <p role="alert" className="mt-6 border-l-2 border-[var(--alert)] pl-3 text-[var(--alert)]">
          {fehlerText(einreichen.error)}
        </p>
      )}

      <Tabelle
        beschriftung="Abrechenbare Provisionen"
        kopf={
          <tr>
            <th scope="col">Auswahl</th>
            <th scope="col">Position</th>
            <th scope="col">Leistungsdatum</th>
            <th scope="col" className="zahl">Betrag</th>
          </tr>
        }
        fuss={
          <>
            <tr>
              <th scope="row" colSpan={3}>Netto</th>
              <td className="zahl"><Betrag cents={ansicht.net_cents} /></td>
            </tr>
            {/* Die Steuerzeile hängt an `vat_mode`, nicht am rohen Satz — genau wie im Dokument
                (src/worker/pdf/rechnung.ts: „Driven off vatMode … never off the raw rate"). Ein
                Kleinunternehmer sähe sonst hier eine Zeile „Umsatzsteuer 0 %", die auf seiner
                Rechnung nie steht, und nicht den Satz, der dort stehen wird. */}
            {ansicht.vat_mode === 'regelbesteuert' && (
              <tr>
                <th scope="row" colSpan={3}>{VAT_19_LABEL}</th>
                <td className="zahl"><Betrag cents={ansicht.vat_cents} /></td>
              </tr>
            )}
            <tr>
              <th scope="row" colSpan={3}>Brutto</th>
              <td className="zahl"><Betrag cents={ansicht.gross_cents} /></td>
            </tr>
          </>
        }
      >
        {ansicht.lines.length === 0 && (
          <TabellenZustand
            art="leer"
            spalten={4}
            titel="Keine abrechenbaren Positionen"
            hinweis={
              'Abrechenbar ist eine Provision, sobald die Zahlung eingegangen ist und ein '
              + 'Zahlungsdatum trägt. Ihre offenen Positionen sehen Sie unter Provisionen.'
            }
          />
        )}
        {ansicht.lines.map((zeile) => {
          const negativ = zeile.amount_cents < 0
          return (
            <tr key={zeile.commission_id}>
              <td>
                <input
                  type="checkbox"
                  // Die Beschriftung ist der Positionstext des Servers — dieselbe Zeichenkette, die
                  // die Rechnung als „Art und Umfang" (§ 14 Abs. 4 UStG) druckt, samt der Zitierung
                  // „Korrektur zu Rechnung …" bei einer Rückforderung. Eine eigene Fassung hier
                  // hätte die Zitierung verschluckt: eine Korrektur sähe aus wie die Provision, die
                  // sie zurücknimmt.
                  aria-label={zeile.description}
                  checked={zeile.ausgewaehlt}
                  // Negative Zeilen nimmt upsertDraft ohnehin immer auf; sie hier abwählbar zu
                  // zeigen wäre ein Versprechen, das der Server nicht hält.
                  disabled={negativ || !zeile.abrechenbar || auswahl.isPending}
                  onChange={() => umschalten(zeile)}
                />
              </td>
              <td>
                {zeile.description}
                {negativ && (
                  <span className="block text-sm leise">
                    Korrekturen werden immer mit abgerechnet und können nicht abgewählt werden.
                  </span>
                )}
                {!zeile.abrechenbar && (
                  <span className="block text-sm text-[var(--alert)]">
                    {zeile.wartet_auf_bestaetigung
                      ? 'Noch nicht abrechenbar: der Auftraggeber hat diese Provision noch nicht bestätigt.'
                      : 'Noch nicht abrechenbar: Zahlungseingang beim Auftraggeber noch nicht erfasst.'}
                  </span>
                )}
              </td>
              <td>{zeile.leistungsdatum === null ? '—' : datum(zeile.leistungsdatum)}</td>
              <td className="zahl"><Betrag cents={zeile.amount_cents} /></td>
            </tr>
          )
        })}
      </Tabelle>

      {/* Was bei `regelbesteuert` die Steuerzeile ist, ist bei den beiden anderen Modi ein Satz unter
          den Summen — dieselbe Aufteilung, die das Dokument macht (`vatNotes`). Der Wortlaut kommt
          aus derselben Funktion, die `submitInvoice` als `legal_text` einfriert und der Renderer
          druckt; hier wird er nicht ein zweites Mal formuliert. */}
      {ansicht.vat_mode !== 'regelbesteuert' && (
        <p className="mt-2 max-w-prose text-sm leise">{vatLegalText(ansicht.vat_mode)}</p>
      )}

      {!ansicht.profil.complete && (
        <section className="mt-8 border border-[var(--rule)] bg-[var(--paper-sunk)] p-4">
          <h2 id="fehlt-abrechnung">
            Es fehlt noch
          </h2>
          <ul aria-labelledby="fehlt-abrechnung" className="mt-2 list-disc pl-5">
            {ansicht.profil.fehlt.map((eintrag) => (
              <li key={eintrag}>{eintrag}</li>
            ))}
          </ul>
          <p className="mt-2">
            <a className="text-[var(--accent)] underline" href="/me/profil">Profil vervollständigen</a>
          </p>
        </section>
      )}

      {gruende.length > 0 && (
        <ul className="mt-8 list-disc pl-5 text-[var(--alert)]">
          {gruende.map((grund) => (
            <li key={grund}>{grund}</li>
          ))}
        </ul>
      )}

      {ansicht.invoice_id !== null && (
        <section className="mt-8">
          <h2>Vorschau</h2>
          {vorschauMoeglich ? (
            <p className="mt-1 max-w-prose text-sm leise">
              {/* Der Pfad wird hier gebildet und nicht gerechnet: er hat kein Ergebnis, nur eine Adresse. */}
              <a
                className="text-[var(--accent)] underline"
                href={`/api/me/rechnungen/${encodeURIComponent(ansicht.invoice_id)}/preview.pdf`}
                target="_blank"
                rel="noreferrer"
              >
                Vorschau als PDF öffnen
              </a>
              {' '}— mit dem Wasserzeichen „VORSCHAU – keine Rechnung“ und ohne Nummer. Die fertige
              Rechnung mit Nummer sehen Sie unter „Meine Abrechnungen“, sobald {ansicht.tenant_name}
              {' '}freigegeben hat.
            </p>
          ) : (
            <p className="mt-1 max-w-prose text-sm leise">
              Eine Vorschau gibt es erst, wenn diese Abrechnung auch eingereicht werden könnte: der
              Server zeichnet keine Seite, auf der eine Pflichtangabe fehlt. Was auf dem Dokument
              stehen wird, steht solange vollständig in der Tabelle oben und in der Erklärung unten —
              dieselben Positionstexte, dieselben Beträge, derselbe Steuerhinweis, derselbe Satz.
            </p>
          )}
        </section>
      )}

      <section className="mt-8">
        {/* Der Satz kommt fertig vom Server (`consent_preview`, aus derselben Funktion, die
            submitInvoice gleich in consent_text einfriert). Er wird hier nicht zusammengesetzt:
            wer einen Haken setzt, soll genau den Satz sehen, der auf dem Dokument landet. */}
        <label className="flex max-w-prose items-baseline gap-2">
          <input
            type="checkbox"
            checked={einverstanden}
            onChange={(ereignis) => setEinverstanden(ereignis.target.checked)}
          />
          <span>{ansicht.consent_preview}</span>
        </label>
        <Knopf
          disabled={!bereit || !einverstanden || einreichen.isPending}
          onClick={() => einreichen.mutate()}
        >
          Abrechnung einreichen
        </Knopf>
      </section>
    </>
  )
}

export function AbrechnenScreen({ tc, alle = false }: { tc: string; alle?: boolean }): ReactElement {
  const entwurf = useQuery({
    queryKey: ['me', 'abrechnung', tc],
    queryFn: () => entwurfLesen(tc),
  })

  return (
    <div className="seite">
      <h1>Abrechnung erstellen</h1>

      {/*
        Nur „lädt" und „Fehler": leer kann diese Antwort nicht sein — der Server liefert immer eine
        Ansicht, notfalls mit null Positionen, und die leere Positionsliste sagt in der Tabelle
        selbst, was ihr fehlt.
      */}
      {entwurf.isError && (
        <Zustand art="fehler" fehler={entwurf.error} onWiederholen={() => void entwurf.refetch()} />
      )}
      {entwurf.isPending && (
        <Zustand art="laedt" beschriftung="Ihre offenen Positionen werden geladen." />
      )}
      {entwurf.data !== undefined && <AbrechnungFormular tc={tc} ansicht={entwurf.data} alleVorwaehlen={alle} />}
    </div>
  )
}

function AbrechnenInhalt(): ReactElement {
  const { tc, alle } = Route.useSearch()
  if (tc === undefined) {
    return (
      <div className="seite">
        <h1>Abrechnung erstellen</h1>
        <p role="status" className="mt-6">
          Bitte wählen Sie unter{' '}
          <a className="text-[var(--accent)] underline" href="/me/provisionen">Meine Provisionen</a>, für
          welchen Auftraggeber Sie abrechnen möchten.
        </p>
      </div>
    )
  }
  return <AbrechnenScreen tc={tc} alle={alle === '1'} />
}

/** Wie bei den übrigen /me-Bildschirmen steht der Zugangsschutz an der Route, nicht im Screen. */
function AbrechnenRoute(): ReactElement {
  return (
    <RequireSession>
      <AbrechnenInhalt />
    </RequireSession>
  )
}

export const Route = createFileRoute('/me/abrechnen')({
  validateSearch: (roh: Record<string, unknown>): AbrechnenSuche => ({
    tc: typeof roh['tc'] === 'string' && roh['tc'] !== '' ? roh['tc'] : undefined,
    alle: roh['alle'] === '1' || roh['alle'] === 1 ? '1' : undefined,
  }),
  component: AbrechnenRoute,
})
